// @ts-strict-ignore
/**
 * @fileoverview SelectController is a wrapper for beacon select components
 * to play well with react-hook-form
 */
import { useController } from "react-hook-form";
import type { Path } from "react-hook-form";

import getUuid from "uuid-by-string";

import { stableValueHash } from "@services/useDashboardApi";

import type { SelectControllerProps } from "./types";
import { optionValueForSelect } from "./utils";

export const SelectController = <
  TFieldValues extends Record<string, any> = Record<string, any>,
  TName extends Path<TFieldValues> = Path<TFieldValues>,
>({
  control,
  name,
  render,
  options,
  isMulti,
  defaultValue,
  rules,
  onChange: onChangeCallback,
}: SelectControllerProps<TFieldValues, TName>): React.ReactElement => {
  const {
    field: { ref, onChange, ...field },
    fieldState: { error },
  } = useController({ control, name, defaultValue, rules });

  const isEqualValue = (value: any, other: any) => {
    if (typeof value === "object" && other) {
      return getUuid(stableValueHash(value)) === getUuid(stableValueHash(other));
    }
    return value === other;
  };

  let value;
  if (options) {
    let flatOptions;
    if (options[0]?.options) {
      flatOptions = options.reduce((prev, curr) => prev.concat(curr.options), []);
    } else {
      flatOptions = options;
    }
    value = isMulti
      ? flatOptions.filter((o) => field.value?.includes(o.value))
      : flatOptions.find(({ value }) => isEqualValue(value, field.value));
  } else {
    const isArray = Array.isArray(field.value);
    if (isMulti) {
      value = isArray
        ? field.value.map((val) => optionValueForSelect(val))
        : [field.value].map((val) => optionValueForSelect(val));
    } else {
      value = isArray ? optionValueForSelect(field.value?.[0] ?? null) : optionValueForSelect(field.value);
    }
  }

  const handleChange = (e) => {
    const value = isMulti ? (e.map((o) => o.value) ?? []) : (e?.value ?? null);

    onChange(value);
    onChangeCallback?.(value);
  };

  // Selects don't clear the value when it's undefined
  if (value === undefined) {
    value = null;
  }

  return render({ field, error, value, onChange: handleChange });
};
